import { gql, useQuery } from '@apollo/client';
import { PaymentMethodType } from '@/enums';
import {
  Payments_GetKlarnaPaymentMethodsQuery,
  Payments_GetKlarnaPaymentMethodsQueryVariables,
} from '@/types';
import { useMemo } from 'react';
import type { KlarnaVariant } from '@/hooks/payments/klarna-types';

export const GET_KLARNA_PAYMENT_METHODS_QUERY = gql`
  query Payments_GetKlarnaPaymentMethods(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getAdyenDropinConfig(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      klarna_payment_methods
    }
  }
`;

interface KlarnaPaymentMethodsParams {
  isKlarnaEnabled: boolean;
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Loader hook for fetching the available Klarna payment methods for this
 * distributor.
 *
 * @param params.isKlarnaEnabled - Whether Klarna is offered (e.g. not a
 *   distributor user, not partial-pay). When false, skips the fetch.
 * @param params.restaurantUUID - The UUID of the restaurant paying.
 * @param params.supplierUUID - The UUID of the distributor being paid.
 * @returns The list of Klarna payment methods, loading state, and any error.
 *
 * @example
 * const { klarnaPaymentMethods, loading } = useKlarnaPaymentMethods({
 *   isKlarnaEnabled,
 *   restaurantUUID,
 *   supplierUUID,
 * });
 */
export const useKlarnaPaymentMethods = ({
  isKlarnaEnabled,
  restaurantUUID,
  supplierUUID,
}: KlarnaPaymentMethodsParams) => {
  const { data, error, loading } = useQuery<
    Payments_GetKlarnaPaymentMethodsQuery,
    Payments_GetKlarnaPaymentMethodsQueryVariables
  >(GET_KLARNA_PAYMENT_METHODS_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !isKlarnaEnabled,
    variables: { restaurantUUID, supplierUUID },
  });

  const klarnaPaymentMethods = useMemo<KlarnaVariant[]>(() => {
    const methods = data?.getAdyenDropinConfig?.klarna_payment_methods ?? [];
    return methods.filter(
      (m): m is KlarnaVariant =>
        m === PaymentMethodType.KLARNA ||
        m === PaymentMethodType.KLARNA_ACCOUNT,
    );
  }, [data]);

  return {
    error,
    klarnaPaymentMethods,
    loading,
  };
};
